import {
  CATALOG_SCHEMA_VERSION,
  GAME_STATUSES,
  INPUT_METHODS,
  type Catalog,
  type ControlHint,
  type Game,
  type GameStatus,
  type InputMethod,
} from './types'

/**
 * Runtime validation for catalog JSON. The catalog is external data (and will
 * eventually be published independently of the website), so it is never
 * trusted as-is.
 *
 * A malformed catalog throws. A malformed individual game is skipped with a
 * warning so one bad entry cannot take down the whole library.
 */

export class CatalogError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CatalogError'
  }
}

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const RATIO_PATTERN = /^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/

type Json = Record<string, unknown>

function isObject(value: unknown): value is Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function fail(path: string, expected: string): never {
  throw new CatalogError(`${path}: expected ${expected}`)
}

function str(obj: Json, key: string, path: string): string {
  const value = obj[key]
  if (typeof value !== 'string' || value.trim() === '') fail(`${path}.${key}`, 'a non-empty string')
  return value.trim()
}

function optionalStr(obj: Json, key: string, path: string): string | null {
  return obj[key] === undefined || obj[key] === null ? null : str(obj, key, path)
}

function date(obj: Json, key: string, path: string): string {
  const value = str(obj, key, path)
  if (!DATE_PATTERN.test(value) || Number.isNaN(Date.parse(value))) fail(`${path}.${key}`, 'a YYYY-MM-DD date')
  return value
}

function optionalDate(obj: Json, key: string, path: string): string | null {
  return obj[key] === undefined || obj[key] === null ? null : date(obj, key, path)
}

function array(obj: Json, key: string, path: string): unknown[] {
  const value = obj[key] ?? []
  if (!Array.isArray(value)) fail(`${path}.${key}`, 'an array')
  return value
}

function object(obj: Json, key: string, path: string): Json {
  const value = obj[key] ?? {}
  if (!isObject(value)) fail(`${path}.${key}`, 'an object')
  return value
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], path: string): T {
  if (typeof value !== 'string' || !allowed.includes(value as T)) fail(path, `one of ${allowed.join(', ')}`)
  return value as T
}

function slug(value: unknown, path: string): string {
  if (typeof value !== 'string' || !SLUG_PATTERN.test(value)) fail(path, 'a lowercase slug (a-z, 0-9, dashes)')
  return value
}

function parseAspectRatio(value: unknown, path: string): number | null {
  if (value === undefined || value === null) return null
  const match = typeof value === 'string' ? RATIO_PATTERN.exec(value) : null
  const width = Number(match?.[1])
  const height = Number(match?.[2])
  if (!match || !(width > 0) || !(height > 0)) fail(path, 'a "width:height" string such as "16:9"')
  return width / height
}

function parseControlHint(value: unknown, path: string): ControlHint {
  if (!isObject(value)) fail(path, 'an object')
  const keys = array(value, 'keys', path).map((key, i) => {
    if (typeof key !== 'string' || key === '') fail(`${path}.keys[${i}]`, 'a non-empty string')
    return key
  })
  if (keys.length === 0) fail(`${path}.keys`, 'at least one key')
  return { keys, action: str(value, 'action', path) }
}

export function parseGame(value: unknown, path: string): Game {
  if (!isObject(value)) fail(path, 'an object')

  const entry = object(value, 'entry', path)
  const controls = object(value, 'controls', path)
  const genres = array(value, 'genres', path).map((genre, i) => slug(genre, `${path}.genres[${i}]`))
  if (genres.length === 0) fail(`${path}.genres`, 'at least one genre')

  return {
    id: slug(value.id, `${path}.id`),
    title: str(value, 'title', path),
    tagline: str(value, 'tagline', path),
    description: str(value, 'description', path),
    developer: str(value, 'developer', path),
    genres: [...new Set(genres)],
    thumbnail: optionalStr(value, 'thumbnail', path),
    version: optionalStr(value, 'version', path),
    entry: {
      url: optionalStr(entry, 'url', `${path}.entry`),
      aspectRatio: parseAspectRatio(entry.aspectRatio, `${path}.entry.aspectRatio`),
      readySignal: entry.readySignal === true,
    },
    controls: {
      inputs: array(controls, 'inputs', `${path}.controls`).map(
        (input, i): InputMethod => oneOf(input, INPUT_METHODS, `${path}.controls.inputs[${i}]`),
      ),
      hints: array(controls, 'hints', `${path}.controls`).map((hint, i) =>
        parseControlHint(hint, `${path}.controls.hints[${i}]`),
      ),
    },
    status: oneOf<GameStatus>(value.status, GAME_STATUSES, `${path}.status`),
    releaseDate: optionalDate(value, 'releaseDate', path),
    addedAt: date(value, 'addedAt', path),
  }
}

export function parseCatalog(value: unknown): Catalog {
  if (!isObject(value)) fail('catalog', 'an object')
  if (value.schemaVersion !== CATALOG_SCHEMA_VERSION) {
    fail('catalog.schemaVersion', `schema version ${CATALOG_SCHEMA_VERSION}`)
  }

  const games: Game[] = []
  const seen = new Set<string>()
  array(value, 'games', 'catalog').forEach((raw, i) => {
    try {
      const game = parseGame(raw, `catalog.games[${i}]`)
      if (seen.has(game.id)) throw new CatalogError(`catalog.games[${i}].id: duplicate id "${game.id}"`)
      seen.add(game.id)
      if (game.status !== 'hidden') games.push(game)
    } catch (error) {
      if (!(error instanceof CatalogError)) throw error
      console.warn(`[catalog] Skipping invalid game entry — ${error.message}`)
    }
  })

  const featured = optionalStr(value, 'featured', 'catalog')
  return {
    schemaVersion: CATALOG_SCHEMA_VERSION,
    featured: featured && games.some((game) => game.id === featured) ? featured : null,
    games,
  }
}
